// La ligne d'audit d'un dossier adapté — partagée extension ↔ console.
//
// C'est l'argument de confiance : pour une consultance qui envoie ce document
// à un client, savoir exactement ce que l'IA a touché vaut plus que la qualité
// de la réécriture elle-même.

import type { DiffStats, FidelityScore } from '@ai-job-fit/engine/resumeDiff'
import type { TranslateFn } from './context'

export interface AuditBadgeProps {
  stats: DiffStats
  fidelity: FidelityScore
  /** Vrai quand le dossier a été produit dans une autre langue que le CV
   *  maître. Le diff apparie les phrases par similarité de TEXTE : une phrase
   *  traduite ne s'apparie plus, donc les compteurs surestiment les
   *  ajouts/retraits et la fidélité affichée est pessimiste. Le dire est plus
   *  honnête que d'afficher un chiffre que l'on sait faux. */
  translated?: boolean
  /** Libellés de l'app hôte. Le badge était écrit en français en dur, et
   *  s'affichait tel quel dans Radar en anglais (2026-10-05). */
  t: TranslateFn
}

/** Mêmes morceaux que le bandeau du diff (`PreviewModal`) : les puces, puis
 *  l'accroche et les compétences, qui n'entrent pas dans les compteurs. */
export function auditParts(stats: DiffStats, t: TranslateFn): string[] {
  const lines = stats.reworded + stats.added + stats.removed + stats.reordered
  const parts = [
    ...(lines > 0
      ? [t('preview.diff.counter', { reworded: stats.reworded, added: stats.added, removed: stats.removed, reordered: stats.reordered })]
      : []),
    ...(stats.summaryChanged ? [t('preview.diff.summary')] : []),
    ...(stats.skillsChanged ? [t('preview.diff.skills', { n: stats.skillsChanged })] : []),
  ]
  return parts.length ? parts : [t('preview.diff.none')]
}

export function AuditBadge({ stats, fidelity, translated, t }: AuditBadgeProps) {
  return (
    <span className={`audit${translated ? ' audit-warn' : ''}`}>
      <b>{t('audit.fidelity', { n: fidelity.score })}</b>
      {' · '}
      {auditParts(stats, t).join(' · ')}
      {translated && <em className="audit-note"> {t('audit.translated')}</em>}
    </span>
  )
}

/** Une traduction entre le CV maître et le document : `paired` dit si le diff
 *  a su apparier les phrases traduites (Radar, par embeddings) ou non
 *  (extension, comparaison mot à mot). */
export interface Traduction {
  /** Nom de la langue du document, prêt à afficher (« français »). */
  to: string
  paired: boolean
}

/**
 * Ce qui a changé, en UNE phrase (issue #178). Remplace trois compteurs qui
 * disaient la même chose (badge, puce « % de ton profil », bandeau orange).
 * Une traduction non appariée ne donne aucun chiffre : compter des phrases
 * traduites comme retirées puis ajoutées affichait « 50 % de ton profil » en
 * orange pour un CV où rien n'était perdu.
 */
export function phraseChangements(
  stats: DiffStats,
  fidelity: FidelityScore | undefined,
  t: TranslateFn,
  traduction?: Traduction
): string {
  const phrases: string[] = []
  if (traduction) phrases.push(t('preview.changes.translated', { lang: traduction.to }))
  if (traduction && !traduction.paired) {
    phrases.push(t('preview.changes.unpaired'))
    return phrases.join(' ')
  }
  const n = (v: number, un: string, plusieurs: string) => (v === 1 ? t(un) : t(plusieurs, { n: v }))
  const morceaux = [
    ...(stats.reworded ? [n(stats.reworded, 'preview.changes.reworded.one', 'preview.changes.reworded')] : []),
    ...(stats.added ? [n(stats.added, 'preview.changes.added.one', 'preview.changes.added')] : []),
    ...(stats.removed ? [n(stats.removed, 'preview.changes.removed.one', 'preview.changes.removed')] : []),
    ...(stats.reordered ? [n(stats.reordered, 'preview.changes.reordered.one', 'preview.changes.reordered')] : []),
    ...(stats.summaryChanged ? [t('preview.changes.summary')] : []),
    ...(stats.skillsChanged ? [n(stats.skillsChanged, 'preview.changes.skills.one', 'preview.changes.skills')] : []),
  ]
  if (morceaux.length === 0) phrases.push(t('preview.diff.none'))
  else {
    const texte = morceaux.join(', ')
    phrases.push(texte.charAt(0).toUpperCase() + texte.slice(1) + '.')
  }
  if (fidelity && fidelity.total > 0) {
    phrases.push(
      fidelity.retained >= fidelity.total
        ? t('preview.changes.allKept')
        : fidelity.total - fidelity.retained === 1
          ? t('preview.changes.lost.one', { total: fidelity.total })
          : t('preview.changes.lost', { n: fidelity.total - fidelity.retained, total: fidelity.total })
    )
  }
  return phrases.join(' ')
}
